import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Alert,
  Stack,
  IconButton,
  Tooltip,
  Paper,
  Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAuth } from '../../contexts/AuthContext';
import type { FamilyMember } from '../../types/user';
import { memberRelationshipLabel } from '../../types/memberRelationships';
import { deleteMember } from '../../services/membersApi';
import MemberForm from '../../components/members/MemberForm';

/**
 * D8 — FamilyMembersPage
 *
 * Lists the family's members (angel-free via sanitizeUser). Members the family
 * added themselves via the App (`familyAdded`) can be edited or removed here;
 * staff-added members (warriors, existing parents) are read-only. After any
 * change the roster is re-pulled via refreshUser().
 */
const FamilyMembersPage: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const familyId = user?.familyMembers?.find((m) => m.crmFamilyId)?.crmFamilyId ?? null;
  const members: FamilyMember[] = user?.familyMembers ?? [];

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<FamilyMember | null>(null);
  const [removing, setRemoving] = useState<FamilyMember | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      await refreshUser();
    } catch {
      // refreshUser is best-effort; the roster will catch up on next load.
    }
  }, [refreshUser]);

  // Foreground refetch so another device's change surfaces.
  useEffect(() => {
    const handler = () => {
      if (!document.hidden) void refresh();
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, [refresh]);

  const handleRemove = async () => {
    if (!removing || !familyId) return;
    setBusy(true);
    setError(null);
    try {
      await deleteMember(familyId, removing.id);
      setRemoving(null);
      await refresh();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to remove family member');
    } finally {
      setBusy(false);
    }
  };

  if (!familyId) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="info">
          Your account isn't linked to a family record yet. Once it is, you'll be able to manage
          your family members here.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
          Family Members
        </Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={() => void refresh()} aria-label="Refresh family members">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Add grandparents, step-parents, carers, siblings and other family members yourself. You can
        edit or remove the members you add; members added by Team Jak are shown here but managed by
        the Team Jak team.
      </Typography>

      <Stack spacing={1.5}>
        {members.map((member) => (
          <Paper key={member.id} variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography sx={{ fontWeight: 600 }}>{member.name}</Typography>
                <Chip
                  size="small"
                  label={memberRelationshipLabel(member.relationship)}
                  sx={{ mt: 0.5 }}
                />
              </Box>
              {member.familyAdded && (
                <Stack direction="row" spacing={0.5}>
                  <Tooltip title="Edit">
                    <IconButton aria-label={`Edit ${member.name}`} onClick={() => setEditing(member)}>
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Remove">
                    <IconButton aria-label={`Remove ${member.name}`} onClick={() => setRemoving(member)}>
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </Stack>
              )}
            </Stack>
          </Paper>
        ))}
      </Stack>

      <Box sx={{ mt: 2 }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setShowForm(true)}>
          Add family member
        </Button>
      </Box>

      {showForm && (
        <Dialog open onClose={() => setShowForm(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontWeight: 600 }}>Add a family member</DialogTitle>
          <DialogContent>
            <MemberForm
              mode="create"
              familyId={familyId}
              onCancel={() => setShowForm(false)}
              onSuccess={() => {
                setShowForm(false);
                void refresh();
              }}
            />
          </DialogContent>
        </Dialog>
      )}

      {editing && (
        <Dialog open onClose={() => setEditing(null)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontWeight: 600 }}>Edit family member</DialogTitle>
          <DialogContent>
            <MemberForm
              mode="edit"
              familyId={familyId}
              initialValue={editing}
              onCancel={() => setEditing(null)}
              onSuccess={() => {
                setEditing(null);
                void refresh();
              }}
            />
          </DialogContent>
        </Dialog>
      )}

      {removing && (
        <Dialog open onClose={() => (busy ? undefined : setRemoving(null))} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ fontWeight: 600 }}>Remove family member</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Remove <strong>{removing.name}</strong> from your family record? You can add them again
              later if you need to.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRemoving(null)} color="inherit" disabled={busy}>
              Cancel
            </Button>
            <Button onClick={handleRemove} color="error" variant="contained" disabled={busy}>
              Remove
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Container>
  );
};

export default FamilyMembersPage;
